import { Printer } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { makeQr, type QrMatrix, qrSvg, qrTarget } from "../../lib/qr";
import "./qr-print.css";

// Print preview of the QR code (prototype page `qr`, "Suport de masă · A6" / "Autocolant · 5 × 5 cm"), with the
// prototype's sizes, colours and tips. Printing uses the page itself (@media print shows only the card at its
// real size): the app's CSP allows no inline script in a separate print window.

type Format = "tent" | "sticker";
const FORMATS: [Format, string][] = [
  ["tent", "Suport de masă · A6"],
  ["sticker", "Autocolant · 5 × 5 cm"],
];
const TIPS: Record<Format, string[]> = {
  tent: [
    "Tipărește la 100%, fără „potrivire în pagină”.",
    "Codul tipărit să aibă cel puțin 2,5 × 2,5 cm.",
    "Testează scanarea cu două telefoane înainte să pui suporturile pe mese.",
  ],
  sticker: [
    "Autocolant 5 × 5 cm, hârtie mată, ca să nu reflecte lumina.",
    "Lasă o margine albă de cel puțin 4 module în jurul codului.",
    "Testează scanarea înainte de a tipări tirajul complet.",
  ],
};
/** Editorial theme colours of the prototype's print card (T.bg, T.ink). */
const TENT_BG = "#F4EFE7";
const INK = "#211C16";

export function QrPrint({ publicUrl, restaurantName }: { publicUrl: string; restaurantName: string }) {
  const [format, setFormat] = useState<Format>("tent");
  const [matrix, setMatrix] = useState<QrMatrix | null>(null);
  const titleId = useId();
  useEffect(() => {
    let live = true;
    makeQr(qrTarget(publicUrl)).then(
      (m) => live && setMatrix(m),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [publicUrl]);
  const tent = format === "tent";
  const svg = matrix ? `data:image/svg+xml;utf8,${encodeURIComponent(qrSvg(matrix))}` : "";
  const shownUrl = publicUrl.replace(/^https?:\/\//, "");
  return (
    <section className="card stack" style={{ gap: 14 }} aria-labelledby={titleId}>
      <h2 id={titleId} className="h2">
        Pentru tipărire
      </h2>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {FORMATS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            aria-pressed={format === k}
            className={`btn btn-sm ${format === k ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFormat(k)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="qr-print-stage">
        <div
          className={`qr-print-card ${tent ? "tent" : "sticker"}`}
          style={{ background: tent ? TENT_BG : "#FFFFFF", color: INK }}
        >
          {tent && <div className="qr-print-title">{restaurantName}</div>}
          {svg && <img className="qr-print-code" src={svg} alt={`Cod QR către ${shownUrl}`} />}
          <div className="qr-print-cap">Scanează pentru meniu</div>
          {tent && <div className="qr-print-url">{shownUrl}</div>}
        </div>
      </div>
      <ul className="stack" style={{ gap: 4, margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: "20px" }}>
        {TIPS[format].map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      <button
        type="button"
        className="btn btn-secondary"
        style={{ alignSelf: "flex-start" }}
        disabled={!matrix}
        onClick={() => window.print()}
      >
        <Printer size={18} strokeWidth={1.75} aria-hidden="true" />
        Deschide pagina de tipărire
      </button>
    </section>
  );
}
